import type { Editor, Operation } from 'slate';
import { Node, Path } from 'slate';

import type { LocationIdProps } from '../internal';
import { generateLocationId, getDirtyPaths, isLocationIdElement } from '../internal';

export const NOOP = 'noop';
export const INSIDE_NORMALIZATION = new WeakMap<Editor, boolean>();

export function isInsideNormalization(editor: Editor): boolean {
  return !!INSIDE_NORMALIZATION.get(editor);
}

export function doInsideNormalization(editor: Editor, fn: () => void): void {
  const value = isInsideNormalization(editor);
  INSIDE_NORMALIZATION.set(editor, true);
  fn();
  INSIDE_NORMALIZATION.set(editor, value);
}

/**
 * Note: It is important for withApply to be the last editor.apply that is called in the plugin stack.
 */
export function withApply(editor: Editor): Editor {
  const { apply } = editor;

  editor.getDirtyPaths = (op: Operation): Path[] => {
    return getDirtyPaths(editor, op);
  };

  editor.apply = (op: Operation): void => {
    handleSelection(editor, op);

    switch (op.type) {
      case 'split_node': {
        const { path } = op;
        const node = Node.get(editor, path);

        apply(op);
        // Ensure the new half of a split block element has a unique locationId.
        if (isLocationIdElement(node))
          editor.apply({
            newProperties: { locationId: generateLocationId() } satisfies LocationIdProps,
            path: Path.next(path),
            properties: { locationId: node.locationId } satisfies LocationIdProps,
            type: 'set_node',
          });

        break;
      }
      default: {
        apply(op);
        break;
      }
    }
  };

  return editor;
}

function handleSelection(editor: Editor, op: Operation): void {
  switch (op.type) {
    case 'set_node': {
      // Don't clear listMarker in set_node op b/c that is how a user selects/formats the marker.
      if (editor.selection) {
        editor.selection = { ...editor.selection, affinity: 'forward' };
      }
      break;
    }
    default: {
      // Clear listMarker in default case and prefer affinity: 'forward'.
      if (op.type === 'set_selection' && op.newProperties) {
        // Back-fill with forward affinity if not set.
        if (op.newProperties) op.newProperties.affinity ??= 'forward';
      }

      if (editor.selection)
        editor.selection = { ...editor.selection, affinity: 'forward', listMarker: null };
      break;
    }
  }
}
